"use client";

import { Check, ChevronDown, Search } from "lucide-react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { type InputRequirement, REQUIREMENT_CLASS } from "./TextInput";

type SelectOption = {
    value: string;
    label: string;
};

type Props = {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    /** 未選択時にトリガーへ薄く出す文言（選択不可のプレースホルダー）。選択可能な「未指定」を出したい場合は options 側に value="" を入れる。 */
    placeholder?: string;
    disabled?: boolean;
    id?: string;
    /** sm=コンパクト（ツールバー）／md=標準（フォーム）。 */
    size?: "sm" | "md";
    fullWidth?: boolean;
    /** 必須=実線枠＋白背景／任意=点線枠＋背景透過。既定は必須。 */
    requirement?: InputRequirement;
    /** 文脈用の追加クラス（flex-1 等。枠線・背景は requirement で表す）。 */
    className?: string;
    ariaLabel?: string;
    /** 選択肢がページングされ、続きがある（末尾に「もっと読む」を出す）。省略時は従来動作。 */
    hasMore?: boolean;
    /** 「もっと読む」押下（続きページの追加ロード）。`hasMore` と対で指定する。 */
    onLoadMore?: () => void;
    /** 追加ロード中（「もっと読む」を無効化・表示切替）。 */
    loadingMore?: boolean;
    /**
     * 検索語をサーバーへ渡す（**渡すと絞り込みが窓の外まで届く**）。
     *
     * 既定の絞り込みは渡された `options` の中を探すだけなので、選択肢がページングされていると
     * 窓の外にあるものは名前を正確に打っても「該当なし」になる（記録 42）。これを渡した呼び出し側は
     * サーバー検索で `options` を差し替える責任を持つ。入力の間引きはこちらで行う。
     */
    onSearch?: (query: string) => void;
};

const SIZE: Record<"sm" | "md", string> = {
    sm: "px-1.5 py-1 text-xs",
    md: "px-2 py-1 text-sm",
};

const OPTION_SIZE: Record<"sm" | "md", string> = {
    sm: "px-2 py-1 text-xs",
    md: "px-2 py-1.5 text-sm",
};

type Anchor = { left: number; width: number; top: number; bottom: number; openUp: boolean };

/**
 * ②のセレクト。option 内をキーワードで絞り込める検索付きコンボボックス。
 * トリガーの見た目は requirement / size で従来 select と一貫。ドロップダウンは
 * portal + fixed で overflow に切られず、↑↓/Enter/Esc・型入力で操作できる。
 */
export function Select({
    value,
    onChange,
    options,
    placeholder,
    disabled,
    id,
    size = "md",
    fullWidth,
    requirement = "required",
    className = "",
    ariaLabel,
    hasMore,
    onLoadMore,
    loadingMore,
    onSearch,
}: Props) {
    const listboxId = useId();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [activeIndex, setActiveIndex] = useState(0);
    const [anchor, setAnchor] = useState<Anchor | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLDivElement>(null);
    /** サーバー検索の間引き（コマンドパレットの検索と同じ 200ms）。 */
    const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const selected = options.find((o) => o.value === value);

    // サーバー検索を使う場合は手元で二重に絞らない（サーバーが返したものがそのまま候補）。
    const filtered = useMemo(() => {
        if (onSearch) return options;
        const q = query.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => o.label.toLowerCase().includes(q));
    }, [options, query, onSearch]);

    const computeAnchor = useCallback(() => {
        const el = triggerRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const spaceBelow = window.innerHeight - r.bottom;
        const openUp = spaceBelow < 280 && r.top > spaceBelow;
        setAnchor({
            left: r.left,
            width: r.width,
            top: r.bottom,
            bottom: window.innerHeight - r.top,
            openUp,
        });
    }, []);

    const openMenu = useCallback(() => {
        if (disabled) return;
        computeAnchor();
        setQuery("");
        // 検索語の表示を空へ戻すので、サーバー検索の窓も無条件の先頭ページへ戻す
        // （前回の検索結果が空の検索欄と一緒に残ると読めない）。
        onSearch?.("");
        const idx = options.findIndex((o) => o.value === value);
        setActiveIndex(idx < 0 ? 0 : idx);
        setOpen(true);
    }, [disabled, computeAnchor, options, value, onSearch]);

    const close = useCallback(() => {
        if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
        setOpen(false);
        triggerRef.current?.focus();
    }, []);

    const choose = useCallback(
        (v: string) => {
            onChange(v);
            setOpen(false);
            triggerRef.current?.focus();
        },
        [onChange],
    );

    // 開いたら検索欄へフォーカス（フォーカス制御＝許可された useEffect 用途）。
    useEffect(() => {
        if (open) searchRef.current?.focus();
    }, [open]);

    // 開いている間だけ祖先スクロール・リサイズへ追従（ブラウザ API 同期）。
    useEffect(() => {
        if (!open) return undefined;
        const handler = () => computeAnchor();
        window.addEventListener("scroll", handler, true);
        window.addEventListener("resize", handler);
        return () => {
            window.removeEventListener("scroll", handler, true);
            window.removeEventListener("resize", handler);
        };
    }, [open, computeAnchor]);

    // アクティブ候補を可視域へ（UI 同期）。
    useEffect(() => {
        if (!open) return;
        listRef.current
            ?.querySelector('[data-active="true"]')
            ?.scrollIntoView({ block: "nearest" });
    }, [open, activeIndex]);

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIndex((i) => Math.min(filtered.length - 1, i + 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(0, i - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            const opt = filtered[activeIndex];
            if (opt) choose(opt.value);
        } else if (e.key === "Escape") {
            e.preventDefault();
            close();
        } else if (e.key === "Tab") {
            setOpen(false);
        }
    };

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                id={id}
                role="combobox"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={open ? listboxId : undefined}
                aria-label={ariaLabel}
                disabled={disabled}
                onClick={() => (open ? setOpen(false) : openMenu())}
                onKeyDown={(e) => {
                    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
                        e.preventDefault();
                        openMenu();
                    }
                }}
                className={`flex items-center gap-1 rounded border ${REQUIREMENT_CLASS[requirement]} text-left text-text-primary outline-none focus:border-text-muted disabled:opacity-50 ${SIZE[size]} ${fullWidth ? "w-full" : ""} ${className}`}
            >
                <span className={`flex-1 truncate ${selected ? "" : "text-text-muted"}`}>
                    {selected ? selected.label : (placeholder ?? "")}
                </span>
                <ChevronDown className="size-3.5 shrink-0 text-text-muted" aria-hidden />
            </button>

            {open &&
                anchor &&
                typeof document !== "undefined" &&
                createPortal(
                    <>
                        <div
                            className="fixed inset-0 z-40"
                            aria-hidden
                            onMouseDown={() => setOpen(false)}
                        />
                        <div
                            className="fixed z-50 flex flex-col overflow-hidden rounded border border-border-subtle bg-surface shadow-lg"
                            style={{
                                left: anchor.left,
                                width: anchor.width,
                                minWidth: anchor.width,
                                ...(anchor.openUp
                                    ? { bottom: anchor.bottom }
                                    : { top: anchor.top }),
                            }}
                        >
                            <div className="flex items-center gap-1 border-b border-border-subtle px-2 py-1">
                                <Search
                                    className="size-3.5 shrink-0 text-text-muted"
                                    aria-hidden
                                />
                                <input
                                    ref={searchRef}
                                    type="text"
                                    value={query}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setQuery(next);
                                        setActiveIndex(0);
                                        if (!onSearch) return;
                                        if (searchTimerRef.current) {
                                            clearTimeout(searchTimerRef.current);
                                        }
                                        searchTimerRef.current = setTimeout(
                                            () => onSearch(next),
                                            200,
                                        );
                                    }}
                                    onKeyDown={onSearchKeyDown}
                                    placeholder="検索…"
                                    aria-label="選択肢を検索"
                                    aria-controls={listboxId}
                                    aria-activedescendant={
                                        filtered[activeIndex]
                                            ? `${listboxId}-opt-${activeIndex}`
                                            : undefined
                                    }
                                    className={`w-full bg-transparent text-text-primary outline-none placeholder:text-text-muted ${size === "sm" ? "text-xs" : "text-sm"}`}
                                />
                            </div>
                            <div
                                ref={listRef}
                                id={listboxId}
                                role="listbox"
                                className="max-h-60 overflow-y-auto py-1"
                            >
                                {filtered.length === 0 ? (
                                    <div className="px-2 py-1.5 text-xs text-text-muted">
                                        該当なし
                                    </div>
                                ) : (
                                    filtered.map((opt, i) => {
                                        const isActive = i === activeIndex;
                                        const isSelected = opt.value === value;
                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                role="option"
                                                id={`${listboxId}-opt-${i}`}
                                                aria-selected={isSelected}
                                                data-active={isActive}
                                                onMouseEnter={() => setActiveIndex(i)}
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    choose(opt.value);
                                                }}
                                                className={`flex w-full items-center gap-1.5 text-left transition-colors ${OPTION_SIZE[size]} ${isActive ? "bg-brand-light" : ""} ${isSelected ? "text-text-primary" : "text-text-secondary"}`}
                                            >
                                                <Check
                                                    className={`size-3.5 shrink-0 text-brand ${isSelected ? "opacity-100" : "opacity-0"}`}
                                                    aria-hidden
                                                />
                                                <span className="flex-1 truncate">
                                                    {opt.label}
                                                </span>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                            {hasMore && onLoadMore && (
                                <div className="border-t border-border-subtle">
                                    <button
                                        type="button"
                                        disabled={loadingMore}
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            if (!loadingMore) onLoadMore();
                                        }}
                                        className={`w-full text-center text-text-secondary transition-colors hover:bg-brand-light disabled:opacity-50 ${OPTION_SIZE[size]}`}
                                    >
                                        {loadingMore ? "読み込み中…" : "もっと読む"}
                                    </button>
                                </div>
                            )}
                        </div>
                    </>,
                    document.body,
                )}
        </>
    );
}
